/**
 * "Open this page with its form already showing" — and, since 2026-09-07,
 * already filled in.
 *
 * ⚠️ IT EXISTS BECAUSE THE REQUESTS CENTER'S BUTTONS LANDED ON A LIST (Josh,
 * 2026-09-05: "when you click +loss runs, it takes you straight to new loss runs
 * page"). Those pages open on the list with the form behind a button — correct
 * when the list is why you came, wrong when you have just pressed "+ Loss Runs".
 *
 * ⚠️ AND IT CARRIES PREFILL, WHICH IS HOW CONNOR STARTS A PROGRAMME (Josh:
 * "I want Connor conversational and to be able to start every programme's
 * process via Connor"). The decision behind that is deliberate and worth
 * keeping: Connor collects the two or three facts that identify the work and
 * then opens the real page holding them. He does NOT rebuild the loss run form
 * inside a 380px chat bubble — a wizard squeezed into a bubble is worse than
 * the page built for it, and the page is where validation, the carrier contact
 * book and the letter preview already live.
 *
 * ⚠️ WHAT TRAVELS IS ONLY EVER IDENTIFYING TEXT — an insured name, a carrier
 * name. Nothing here carries a limit, a date, an address or an email, because a
 * value that arrives prefilled is a value somebody stops reading. Two fields
 * they still have to check is the point at which it saves work without
 * quietly deciding anything.
 */
const PARAM = 'new';

/** The fields Connor may hand over. Anything else is ignored on arrival. */
export interface Prefill {
  insured?: string;
  carrier?: string;
}

/** The href that opens a programme straight onto its form. */
export function newFormHref(href: string, prefill?: Prefill): string {
  const q = new URLSearchParams({ [PARAM]: '1' });
  if (prefill?.insured) q.set('insured', prefill.insured);
  if (prefill?.carrier) q.set('carrier', prefill.carrier);
  return `${href}?${q.toString()}`;
}

/**
 * Whether this page was opened asking for its form.
 *
 * Read in a useState initialiser rather than an effect, so the form is showing
 * on the first paint — an effect would render the list for a frame and then
 * replace it, which reads as a flicker on a page you asked to skip.
 */
export const openedOnForm = (): boolean => {
  try {
    return new URLSearchParams(window.location.search).get(PARAM) === '1';
  } catch {
    return false;
  }
};

/**
 * The values this page was opened with.
 *
 * ⚠️ TRIMMED AND LENGTH-CAPPED. These arrive in a URL, which means they arrive
 * from anywhere — a pasted link, a bookmark, somebody's autocomplete. They only
 * ever reach a text input the user can see and edit, but a 4,000-character
 * insured name filling a form field is a mess nobody expects to have to undo.
 */
export function prefill(): Prefill {
  try {
    const q = new URLSearchParams(window.location.search);
    const take = (k: string) => (q.get(k) ?? '').trim().slice(0, 200) || undefined;
    return { insured: take('insured'), carrier: take('carrier') };
  } catch {
    return {};
  }
}
